import * as React from 'react';
import { useMemo, useRef } from 'react';

import { useDebounce } from 'use-debounce';
import { generateHTML } from '@tiptap/core';

import { BASE_CONFIG } from '@components/shared/Tiptap/constants';
import { buildExtensions, Config } from '@components/shared/Tiptap/helpers/extensions';

import { parsePartialJson } from './parsePartialJson';

interface Props {
  document: ApiDocument['doc']; // ProseMirror JSON
  config?: Partial<Config>;
  className?: string;
}

const parseDocumentInput = (document: unknown): unknown => {
  if (typeof document === 'string') {
    return parsePartialJson(document);
  }
  return document;
};

const isValidProseMirrorDocument = (parsedDocument: unknown): boolean => {
  return Boolean(
    parsedDocument &&
      typeof parsedDocument === 'object' &&
      parsedDocument !== null &&
      'type' in parsedDocument &&
      parsedDocument.type === 'doc' &&
      'content' in parsedDocument &&
      Array.isArray(parsedDocument.content)
  );
};

const generateHTMLOutput = (parsedDocument: Record<string, unknown>, config: Partial<Config>): string => {
  const extensions = buildExtensions({ ...BASE_CONFIG, ...config });
  return generateHTML(parsedDocument, extensions);
};

export const TiptapRenderer: React.FC<Props> = ({ document, config = {}, className = '' }) => {
  // Keep track of the last successful render to prevent flickering
  const lastSuccessfulRender = useRef<string>('');

  // Throttle document changes to limit CPU usage - maximum once per 100ms
  const [throttledDocument] = useDebounce(document, 100, { maxWait: 100 });

  const htmlOutput = useMemo(() => {
    const fallbackContent = lastSuccessfulRender.current || '';
    const errorContent =
      lastSuccessfulRender.current ||
      '<div style="display: none;" data-tiptap-render-error="true">Failed to render content</div>';

    if (!throttledDocument) {
      return fallbackContent;
    }

    const parsedDocument = parseDocumentInput(throttledDocument);
    if (!parsedDocument || !isValidProseMirrorDocument(parsedDocument)) {
      return fallbackContent;
    }

    try {
      const result = generateHTMLOutput(parsedDocument as Record<string, unknown>, config);
      lastSuccessfulRender.current = result;
      return result;
    } catch {
      return errorContent;
    }
  }, [throttledDocument, config]);

  if (!htmlOutput) {
    return null;
  }

  return <div className={className} dangerouslySetInnerHTML={{ __html: htmlOutput }} />;
};
